function LegalLayout({ title, eyebrow, children, testid }) {
  return (
    <div data-testid={testid} className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-3xl">
        <p className="eyebrow mb-4">{eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-12">
          {title}
        </h1>
        <div className="prose-legal space-y-8 text-zinc-300 leading-relaxed">
          {children}
        </div>
        <p className="text-xs text-zinc-500 mt-16 uppercase tracking-[0.2em]">
          Last updated: February 2026
        </p>
      </div>
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 className="font-display text-2xl font-medium tracking-tight text-white mt-10 mb-4">
      {children}
    </h2>
  );
}

function P({ children }) {
  return <p className="text-zinc-400 leading-relaxed">{children}</p>;
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" eyebrow="Legal" testid="privacy-page">
      <P>
        Stageletics ("we", "us", "our") respects your privacy. This Privacy
        Policy explains how we collect, use, and protect information when you
        visit stageletics.com or purchase our products.
      </P>

      <H2>Information We Collect</H2>
      <P>
        We collect information you provide directly: name, email address,
        shipping address, billing address, and payment details necessary to
        process your order. We also automatically collect technical data such
        as your IP address, browser type, and pages visited.
      </P>

      <H2>How We Use Your Information</H2>
      <P>
        We use your information to process orders, ship products, communicate
        about your purchase, improve our website, and (with your consent) send
        you updates about new products. We do not sell your personal data.
      </P>

      <H2>Payment Processing</H2>
      <P>
        Payments are processed by Stripe, PayPal, Braintree, or other third
        party providers. We never store your full credit card information on
        our servers. Please review their privacy policies separately.
      </P>

      <H2>Cookies</H2>
      <P>
        We use cookies and similar technologies to maintain your shopping
        cart, remember preferences, and analyze site traffic. You can disable
        cookies in your browser settings.
      </P>

      <H2>Data Sharing</H2>
      <P>
        We share information only with service providers necessary to fulfill
        your order (e.g., shipping carriers, payment processors) and when
        required by law.
      </P>

      <H2>Your Rights</H2>
      <P>
        You may request access to, correction of, or deletion of your personal
        data by contacting us. EU and California residents have additional
        rights under GDPR and CCPA respectively.
      </P>

      <H2>Contact</H2>
      <P>For privacy questions, email privacy@stageletics.com.</P>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" eyebrow="Legal" testid="terms-page">
      <P>
        These Terms of Service govern your use of stageletics.com and the
        purchase of Stageletics products. By using our site or placing an
        order, you agree to these terms.
      </P>

      <H2>Orders &amp; Pricing</H2>
      <P>
        All prices are listed in US Dollars and are subject to change without
        notice. We reserve the right to refuse or cancel any order, including
        for pricing errors, suspected fraud, or inventory limitations. If we
        cancel an order, we will refund any payment received.
      </P>

      <H2>Shipping</H2>
      <P>
        Stageletics manufactures, packages, and ships each order in-house.
        Orders typically ship within 2-3 business days. Title and risk of
        loss pass to you upon delivery to the carrier.
      </P>

      <H2>Intellectual Property</H2>
      <P>
        The Stageletics name, "Aura Sleeve" product name, logos, designs, and
        all website content are owned by Stageletics and protected by US and
        international intellectual property laws. You may not copy or
        reproduce them without written permission.
      </P>

      <H2>Product Use</H2>
      <P>
        Aura Sleeve is designed to cover cables on microphone stands. Use
        outside of this intended purpose is at your own risk. Do not use to
        cover damaged, frayed, or improperly grounded cables.
      </P>

      <H2>Disclaimer of Warranties</H2>
      <P>
        Except for the express 1-year material warranty, products are provided
        "as is" without warranties of any kind, whether express or implied,
        including merchantability or fitness for a particular purpose.
      </P>

      <H2>Limitation of Liability</H2>
      <P>
        To the maximum extent permitted by law, Stageletics' total liability
        for any claim related to a product or this website shall not exceed
        the amount you paid for the product.
      </P>

      <H2>Governing Law</H2>
      <P>
        These Terms are governed by the laws of the United States and the
        state in which Stageletics is registered, without regard to conflict
        of law principles.
      </P>

      <H2>Contact</H2>
      <P>Questions? Email support@stageletics.com.</P>
    </LegalLayout>
  );
}

export function Returns() {
  return (
    <LegalLayout title="Return Policy" eyebrow="Support" testid="returns-page">
      <P>
        We want you to love your Aura Sleeve. If for any reason you don't,
        here's how we handle returns.
      </P>

      <H2>30-Day Return Window</H2>
      <P>
        You have 30 days from the delivery date to request a return. Items
        must be in original, unused condition with all original packaging.
      </P>

      <H2>How to Initiate a Return</H2>
      <P>
        Email returns@stageletics.com with your order number and reason for
        return. We will respond within 1-2 business days with a return
        authorization and shipping instructions.
      </P>

      <H2>Refunds</H2>
      <P>
        Once we receive and inspect your return (usually 3-5 business days
        after we get it), your refund will be processed back to your original
        payment method within 5-10 business days. The original $7.99 flat
        shipping fee is non-refundable.
      </P>

      <H2>Return Shipping</H2>
      <P>
        Customers are responsible for return shipping costs unless the return
        is due to our error (wrong item, defect, etc.), in which case we
        cover shipping in both directions.
      </P>

      <H2>Damaged or Defective Products</H2>
      <P>
        If your Aura Sleeve arrives damaged or has a manufacturing defect
        covered by our 1-year warranty, email us with photos within 7 days
        and we'll arrange a free replacement.
      </P>

      <H2>Non-Returnable Items</H2>
      <P>
        Bulk wholesale orders (10+ units) and custom/promotional units are
        final sale unless defective.
      </P>

      <H2>Contact</H2>
      <P>Questions about a return? Email returns@stageletics.com.</P>
    </LegalLayout>
  );
}

export default Privacy;
